/**
 * §11.16 — the gas clouds.
 *
 * **The request, and the constraint that shapes it.** The brief asked for gas clouds in
 * "bright various colours". §11's palette has no room for one more: the AMD-46 sweep
 * measured every hue on the wheel at every saturation against the 62 meaning-carrying
 * tokens, each simulated through all four §11.12 deficiencies, and the best worst-case
 * OKLab ΔE any new bright colour can reach is **5.0** — against `MIN_RESERVED_DELTA_E`
 * of 20 and the XP family's 32. Restricted to the luminance AMD-6 allows a background
 * element, the ceiling is **13.9**, and the neighbour that caps it is
 * `pal.enemy.basalt-crab`, which sits at almost exactly the luminance a legal cloud does.
 *
 * There is no hue to spend. So these clouds spend none.
 *
 * **Three things carry the colour instead, all of them free.**
 *
 * 1. *The two real dynamic lights* (§14.1 B8). `FRAG_GAS` scatters the player aura and
 *    the boss light through the cloud body under the same AMD-6 D1 headroom clamp
 *    `FLOOR_FRAG` uses. A cloud near the ship glows warm gold, a cloud near the hadal
 *    sovereign glows violet, a cloud in open water stays the blue-grey of the sonar
 *    chrome it is drawn in. Every colour it shows is already in the frame, already
 *    means what it means, and cannot teach a colliding code.
 * 2. *The additive layers above it.* The cloud sits at `LAYER_GAS`, below the glow, the
 *    motes, the bodies and the trails, all of which blend additively over it. A cloud is
 *    a darker, structured backdrop, so everything alive that crosses one reads MORE
 *    saturated, not less.
 * 3. *The bloom.* Nothing here crosses `PostStack.BRIGHT_PASS_FLOOR` on its own — a
 *    cloud must never be a lamp — but the entities inside one still do, and their
 *    halation lands on the cloud rather than on flat black.
 *
 * **Why this is safe without a colour measurement holding.** The separation is
 * structural, and structure does not drift:
 *
 *  - *z-order* — `LAYER_GAS` is 22. Every gameplay batch is 24 or higher, so a body is
 *    never behind a cloud and the two are never ambiguous;
 *  - *scale* — a cloud is 9–22 u across against a 0.6–1.4 u trash body, a ratio of 10–30×;
 *  - *edge* — every enemy wears a 2 px pure-white outline and a cloud's alpha reaches
 *    zero over 40% of its radius. Edges mean "object" in this game, and a cloud has none.
 *
 * **Determinism and cost.** The field is a fixed point set on the AMD-24 torus, hashed
 * once at construction exactly as `scene.ts` builds its parallax layers — no RNG stream
 * at all, so a capture of a given sim second reproduces exactly and the clouds cost
 * nothing from `vfx`. Drift is a constant velocity per cloud evaluated from `simTime`,
 * so there is no integrated state to desync. One draw call; instances outside the view
 * are not pushed.
 */

import * as THREE from 'three';
import { WRAP_H, WRAP_W, wrapDeltaX, wrapDeltaY } from '../sim/wrap';
import { InstancedBatch, LAYER_GAS, MODE_GAS } from './instanced';
import { LINEAR, tokenIndex } from './palette';
import { quality } from './quality';
import { buildDiscGeometry } from './silhouettes';
import { worldWarpUniforms } from './warp';

/**
 * How many clouds exist on the 200 × 200 torus. Sized so a 1080p desktop framing
 * (53 × 30 u, 1.6% of the arena) sees two or three at a time and a 390 px phone
 * (19 × 42 u) sees one or two — weather you fly through, not weather you sit in.
 */
const CLOUD_COUNT = 26;
const CLOUD_R_MIN = 9;
const CLOUD_R_MAX = 22;
/** Drift speed band, u/s. Slower than the slowest enemy, so a cloud never reads as alive. */
const DRIFT_MIN = 0.35;
const DRIFT_MAX = 1.15;

/**
 * The two tokens a cloud can be drawn in — both from the sonar-instrument family, which
 * is the family that already means "this is apparatus, read past it". `pal.ui.range` is
 * a deliberate member of the same set for the same reason (see its doc comment in
 * `palette.ts`), so this is a precedent rather than a new idea.
 *
 * There were three. `pal.ui.grid` was dropped after the first capture pass: at 0.0295
 * raw luminance it needed 98% opacity to clear the abyssal floor by enough to be seen,
 * and a 98% cloud is not a cloud, it is a hole. The two that remain are bright enough to
 * register at an alpha that still lets the lattice read through them.
 *
 * Variety comes from density, scale, drift and the borrowed light, not from hue.
 */
const CLOUD_TOKENS = ['pal.ui.gridmajor', 'pal.ui.decor'] as const;

/**
 * Peak alpha per token, solved so the densest core of either cloud composites to ~0.029
 * linear luminance over the `pal.ui.bg` floor at 0.0035.
 *
 * That is eight times the floor — enough to see, which the first pass at 0.0096 was not
 * — and under half of AMD-6's 0.06 ceiling, which leaves the other half to the two
 * lights `FRAG_GAS` then scatters through the cloud under its own headroom clamp.
 */
const CLOUD_ALPHA: readonly number[] = [0.44, 0.29];
/**
 * No emissive scaling. The alphas above are solved against the tokens' true luminance,
 * so a second multiplier here would only make the two numbers lie to each other — which
 * is exactly what made the first pass invisible.
 */
const CLOUD_EM = 1;

export class GasField {
  readonly batch: InstancedBatch;

  /** Home position on the torus, drift velocity, radius, token and phase, per cloud. */
  private readonly homeX = new Float32Array(CLOUD_COUNT);
  private readonly homeY = new Float32Array(CLOUD_COUNT);
  private readonly velX = new Float32Array(CLOUD_COUNT);
  private readonly velY = new Float32Array(CLOUD_COUNT);
  private readonly radius = new Float32Array(CLOUD_COUNT);
  private readonly token = new Int32Array(CLOUD_COUNT);
  private readonly phase = new Float32Array(CLOUD_COUNT);

  private readonly origin: THREE.Vector2;

  /** §13.1.3 Video → "Gas clouds: on / off". Drives the batch straight to empty. */
  enabled = true;

  constructor(scene: THREE.Scene) {
    this.batch = new InstancedBatch(buildDiscGeometry(20), CLOUD_COUNT, {
      mode: MODE_GAS,
      blend: 'normal',
      renderOrder: LAYER_GAS,
      name: 'gas-cloud',
      uniforms: {
        uLight0Pos: { value: new THREE.Vector2() },
        uLight0Color: { value: new THREE.Vector3() },
        uLight0Range: { value: 12 },
        uLight1Pos: { value: new THREE.Vector2() },
        uLight1Color: { value: new THREE.Vector3() },
        uLight1Range: { value: 0.001 },
        uOrigin: { value: new THREE.Vector2() },
      },
    });
    scene.add(this.batch.mesh);
    this.origin = this.batch.material.uniforms.uOrigin.value as THREE.Vector2;

    // The same sin-hash `scene.ts` seeds its parallax field with: deterministic, stable
    // across every run and every capture, and it draws from no RNG stream.
    for (let i = 0; i < CLOUD_COUNT; i++) {
      const h1 = Math.sin((i + 1) * 12.9898 + 41.13) * 43758.5453;
      const h2 = Math.sin((i + 1) * 39.3468 + 17.71) * 24634.6345;
      const h3 = Math.sin((i + 1) * 4.898 + 91.37) * 9871.234;
      const h4 = Math.sin((i + 1) * 27.611 + 6.29) * 15731.743;
      const h5 = Math.sin((i + 1) * 8.233 + 53.07) * 31337.117;
      const f = (v: number): number => v - Math.floor(v);
      this.homeX[i] = (f(h1) - 0.5) * WRAP_W;
      this.homeY[i] = (f(h2) - 0.5) * WRAP_H;
      this.radius[i] = CLOUD_R_MIN + f(h3) * (CLOUD_R_MAX - CLOUD_R_MIN);
      const bearing = f(h4) * Math.PI * 2;
      const speed = DRIFT_MIN + f(h5) * (DRIFT_MAX - DRIFT_MIN);
      this.velX[i] = Math.cos(bearing) * speed;
      this.velY[i] = Math.sin(bearing) * speed;
      this.token[i] = i % CLOUD_TOKENS.length;
      this.phase[i] = f(h3 * 3.1) * 40;
    }
  }

  /**
   * Mirrors the two real lights into this batch's uniforms. `SceneGraph` calls it from
   * the same setters that drive `FLOOR_FRAG`, so the cloud and the floor are lit by one
   * pair of values and cannot disagree about where the boss is.
   */
  setLight(slot: 0 | 1, x: number, y: number, colorIndex: number, range: number, intensity: number): void {
    const u = this.batch.material.uniforms;
    const pos = (slot === 0 ? u.uLight0Pos : u.uLight1Pos).value as THREE.Vector2;
    const col = (slot === 0 ? u.uLight0Color : u.uLight1Color).value as THREE.Vector3;
    pos.set(x, y);
    const ci = colorIndex * 3;
    col.set(LINEAR[ci] * intensity, LINEAR[ci + 1] * intensity, LINEAR[ci + 2] * intensity);
    (slot === 0 ? u.uLight0Range : u.uLight1Range).value = Math.max(0.001, range);
  }

  /**
   * Rebuilds the visible clouds. `time` is sim-derived seconds — never a wall clock —
   * so a still of a given tick reproduces the field exactly.
   *
   * Degradation rides `quality.parallaxLayers`, the knob the §14.6 ladder already turns
   * for the background field, rather than adding a rung of its own: 3 layers is the full
   * domain-warped shader, 1 drops to a single noise octave, 0 hides the batch outright.
   * A gas cloud carries no gameplay information, so it belongs in the degradable set and
   * this is what makes it degrade rather than drop frames.
   */
  update(camX: number, camY: number, viewW: number, viewH: number, time: number): void {
    this.batch.begin();
    const layers = quality.parallaxLayers;
    if (!this.enabled || layers <= 0) {
      this.batch.end();
      return;
    }
    const octaves = layers >= 3 ? 2 : 1;
    // The instance origin every fragment measures the lights from. One value for the
    // whole batch, so it is the camera focus and each fragment adds its own local offset.
    this.origin.set(camX, camY);

    const cullX = viewW * 0.5;
    const cullY = viewH * 0.5;
    for (let i = 0; i < CLOUD_COUNT; i++) {
      const r = this.radius[i];
      // AMD-24: the drift is applied to the home position and the OFFSET is taken the
      // short way round. Wrapping the drifted position first and subtracting afterwards
      // would step a cloud a full period sideways on the frame its centre crossed the
      // seam — the parallax bug this codebase already paid for once.
      const dx = wrapDeltaX(this.homeX[i] + this.velX[i] * time - camX);
      if (dx < -cullX - r || dx > cullX + r) continue;
      const dy = wrapDeltaY(this.homeY[i] + this.velY[i] * time - camY);
      if (dy < -cullY - r || dy > cullY + r) continue;
      const t = this.token[i];
      this.batch.push(
        camX + dx,
        camY + dy,
        0,
        r,
        r,
        TOKEN_INDEX[t],
        CLOUD_ALPHA[t],
        CLOUD_EM,
        time * 0.08 + this.phase[i],
        octaves,
      );
    }
    this.batch.end();
  }

  dispose(): void {
    this.batch.dispose();
  }
}

/** Resolved once at module load; `update` never looks a token up by string. */
const TOKEN_INDEX: readonly number[] = CLOUD_TOKENS.map((t) => tokenIndex(t));
